import React from 'react';
import { useSelector } from 'react-redux';

import RadioLiveView from '../../../Blocks/RadioLive/RadioLiveView';
import LatestNewsAside from '../../../shared/LatestNewsAside';
import UploadBannerSection from '../../../shared/UploadBannerSection/UploadBannerSection';
import PodcastEpisodesView from '../../../Blocks/PodcastEpisodes/PodcastEpisodesView';

import ScheduleGrid from './components/ScheduleGrid';
import ScheduleDayTabs from './components/ScheduleDayTabs';
import ScheduleStationSelect from './components/ScheduleStationSelect';

import {
  getStations,
  normalizeStations,
} from '../../../../services/api/stations';
import { getPublicResolvedSchedule } from '../../../../services/api/radioSchedule';
import { getStationCurrentDate } from '../../../../utils/stationTime';

import type {
  PublicResolvedSchedulePayload,
  ScheduleStationPayload,
} from '../../../../interfaces/schedule';

import './ScheduleView.less';

import bannerFestival from '../../../../theme/assets/images/banner-festival.png';
import { selectViewData, VIEW_DATA_KEYS } from '../viewSsrData';

type DayOption = {
  id: string;
  label: string;
  date: string;
  dayNumber: string;
};

function formatIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getWeekdayShortLabel(date: Date) {
  const labels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  return labels[date.getDay()];
}

function buildDayOptions(baseDate = new Date()): DayOption[] {
  return Array.from({ length: 14 }).map((_, index) => {
    const nextDate = new Date(baseDate);
    nextDate.setDate(baseDate.getDate() + (index - 1)); // hoje fica na 2ª posição

    return {
      id: formatIsoDate(nextDate),
      date: formatIsoDate(nextDate),
      label: getWeekdayShortLabel(nextDate),
      dayNumber: String(nextDate.getDate()).padStart(2, '0'),
    };
  });
}

function isToday(dateString: string, stationId: string) {
  return dateString === getStationCurrentDate(stationId);
}

function mapStationsToSchedulePayload(
  stations: Awaited<ReturnType<typeof getStations>>,
): ScheduleStationPayload[] {
  return stations.map((station) => ({
    id: station.id,
    title: station.title,
    shortTitle: station.shortName,
    isActive: true,
  }));
}

export default function ScheduleView() {
  const dayOptions = React.useMemo(() => buildDayOptions(), []);
  const dayTabsRef = React.useRef<HTMLDivElement | null>(null);
  const preloadedStations = useSelector((state: any) =>
    selectViewData(state, VIEW_DATA_KEYS.stations),
  );
  const hasPreloadedStations =
    Array.isArray(preloadedStations) || Array.isArray(preloadedStations?.items);
  const initialStations = mapStationsToSchedulePayload(
    normalizeStations(preloadedStations),
  );
  const initialStationId = initialStations[0]?.id || '';
  const lastPlayerStationIdRef = React.useRef<string | null>(null);
  const autoSwitchTimerRef = React.useRef<number | null>(null);

  const playerStationId = useSelector(
    (state: any) => state.player?.currentStationId ?? null,
  );
  const isListeningLive = useSelector(
    (state: any) =>
      state.player?.mode !== 'vod' && !!state.player?.intentPlaying,
  );

  const [stations, setStations] =
    React.useState<ScheduleStationPayload[]>(initialStations);
  const [activeStationId, setActiveStationId] =
    React.useState(initialStationId);
  const [activeDate, setActiveDate] = React.useState(
    getStationCurrentDate(initialStationId),
  );
  const [resolvedSchedule, setResolvedSchedule] =
    React.useState<PublicResolvedSchedulePayload | null>(null);
  const [loadingStations, setLoadingStations] =
    React.useState(!hasPreloadedStations);
  const [loadingSchedule, setLoadingSchedule] = React.useState(false);
  const [error, setError] = React.useState('');
  const [autoSwitchNotice, setAutoSwitchNotice] = React.useState('');
  const [isFullScheduleOpen, setIsFullScheduleOpen] = React.useState(false);

  React.useEffect(
    () => () => {
      if (autoSwitchTimerRef.current) {
        window.clearTimeout(autoSwitchTimerRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (hasPreloadedStations) {
      const mappedStations = mapStationsToSchedulePayload(
        normalizeStations(preloadedStations),
      );
      setStations(mappedStations);
      setLoadingStations(false);

      if (!activeStationId && mappedStations.length) {
        setActiveStationId(mappedStations[0].id);
        setActiveDate(getStationCurrentDate(mappedStations[0].id));
      }
      return;
    }

    let isMounted = true;

    async function loadStations() {
      try {
        setLoadingStations(true);
        const response = await getStations();

        if (!isMounted) return;

        const mappedStations = mapStationsToSchedulePayload(response);
        setStations(mappedStations);

        if (!activeStationId && mappedStations.length) {
          setActiveStationId(mappedStations[0].id);
          setActiveDate(getStationCurrentDate(mappedStations[0].id));
        }
      } catch {
        if (!isMounted) return;
        setError('Não foi possível carregar as emissoras.');
      } finally {
        if (isMounted) {
          setLoadingStations(false);
        }
      }
    }

    loadStations();

    return () => {
      isMounted = false;
    };
  }, [activeStationId, hasPreloadedStations, preloadedStations]);

  React.useEffect(() => {
    if (!stations.length) return;

    const playingStation = isListeningLive
      ? stations.find((station) => station.id === playerStationId)
      : null;

    if (!activeStationId) {
      setActiveStationId(playingStation?.id ?? stations[0].id);
      lastPlayerStationIdRef.current = playingStation?.id ?? null;
      return;
    }

    if (!isListeningLive) {
      lastPlayerStationIdRef.current = null;
      return;
    }

    if (
      playingStation &&
      playingStation.id !== lastPlayerStationIdRef.current
    ) {
      lastPlayerStationIdRef.current = playingStation.id;

      if (isToday(activeDate, activeStationId)) {
        setActiveDate(getStationCurrentDate(playingStation.id));
      }

      setActiveStationId(playingStation.id);
      setAutoSwitchNotice(
        `Grade atualizada para ${playingStation.shortTitle || playingStation.title}`,
      );

      if (autoSwitchTimerRef.current) {
        window.clearTimeout(autoSwitchTimerRef.current);
      }

      autoSwitchTimerRef.current = window.setTimeout(() => {
        setAutoSwitchNotice('');
        autoSwitchTimerRef.current = null;
      }, 3200);
    }
  }, [activeDate, activeStationId, isListeningLive, playerStationId, stations]);

  React.useEffect(() => {
    let isMounted = true;

    async function loadSchedule() {
      if (!activeStationId || !activeDate) return;

      try {
        setLoadingSchedule(true);
        setError('');

        const data = await getPublicResolvedSchedule({
          stationId: activeStationId,
          date: activeDate,
        });

        if (!isMounted) return;

        setResolvedSchedule(data);
      } catch {
        if (!isMounted) return;
        setResolvedSchedule(null);
        setError('Não foi possível carregar a programação.');
      } finally {
        if (isMounted) {
          setLoadingSchedule(false);
        }
      }
    }

    loadSchedule();

    return () => {
      isMounted = false;
    };
  }, [activeStationId, activeDate]);

  const activeStation =
    stations.find((station) => station.id === activeStationId) ??
    stations[0] ??
    null;
  const isFollowingLivePlayer =
    isListeningLive && playerStationId === activeStationId;

  const selectStation = React.useCallback(
    (stationId: string) => {
      if (isToday(activeDate, activeStationId)) {
        setActiveDate(getStationCurrentDate(stationId));
      }

      setActiveStationId(stationId);
    },
    [activeDate, activeStationId],
  );

  return (
    <div className="schedule-view">
      <div className="schedule-view__top">
        <div
          className={`schedule-view__main${
            isFullScheduleOpen ? ' is-schedule-expanded' : ''
          }`}
        >
          <section
            className={`schedule-view__panel${
              autoSwitchNotice ? ' is-auto-switching' : ''
            }`}
          >
            <header className="schedule-view__header">
              <div className="schedule-view__header-left">
                <div className="schedule-view__eyebrow">
                  <span>Programação</span>
                  {isFollowingLivePlayer ? (
                    <span className="schedule-view__live-badge">
                      <span
                        className="schedule-view__live-dot"
                        aria-hidden="true"
                      />
                      Acompanhando o player
                    </span>
                  ) : (
                    <span className="schedule-view__station-badge">
                      Grade da emissora
                    </span>
                  )}
                </div>
                <h1 className="schedule-view__heading">
                  <span className="schedule-view__heading-station">
                    {activeStation?.shortTitle ||
                      activeStation?.title ||
                      'Rádio'}
                  </span>
                </h1>

                {resolvedSchedule?.currentItem ? (
                  <p className="schedule-view__now-playing">
                    Tocando agora:{' '}
                    <strong>{resolvedSchedule.currentItem.title}</strong>
                  </p>
                ) : null}

                <div
                  className={`schedule-view__switch-notice${
                    autoSwitchNotice ? ' is-visible' : ''
                  }`}
                  role="status"
                  aria-live="polite"
                >
                  {autoSwitchNotice}
                </div>
              </div>

              <div className="schedule-view__header-right">
                <ScheduleStationSelect
                  stations={stations}
                  activeStationId={activeStationId}
                  onChange={selectStation}
                />
              </div>
            </header>

            <ScheduleDayTabs
              ref={dayTabsRef}
              days={dayOptions.map((day) => ({
                id: day.id,
                label: day.label,
                dayNumber: day.dayNumber,
              }))}
              activeDayId={activeDate}
              onChange={setActiveDate}
            />

            {loadingStations || loadingSchedule ? (
              <div className="schedule-view__empty">
                Carregando programação...
              </div>
            ) : null}

            {!loadingStations && !loadingSchedule && error ? (
              <div className="schedule-view__empty">{error}</div>
            ) : null}

            {!loadingStations &&
            !loadingSchedule &&
            !error &&
            resolvedSchedule?.items?.length ? (
              <ScheduleGrid
                title="Grade do dia"
                items={resolvedSchedule.items}
                activeStationId={activeStationId}
                stations={stations}
                showNowButton={isToday(activeDate, activeStationId)}
                enableNowPlayingHighlight={isToday(activeDate, activeStationId)}
                onChooseOtherStations={() => {
                  dayTabsRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                onSelectStation={selectStation}
                onExpansionChange={setIsFullScheduleOpen}
              />
            ) : null}

            {!loadingStations &&
            !loadingSchedule &&
            !error &&
            !resolvedSchedule?.items?.length ? (
              <div className="schedule-view__empty">
                Nenhuma programação disponível para a data selecionada.
              </div>
            ) : null}
          </section>
        </div>

        <aside className="schedule-view__aside">
          <div className="schedule-view__aside-block">
            <RadioLiveView
              data={{
                variant: 'compact',
              }}
            />
          </div>

          <div className="schedule-view__aside-block">
            <LatestNewsAside />
          </div>

          <div className="schedule-view__aside-block">
            <UploadBannerSection
              imageUrl={bannerFestival}
              imageWidth={369}
              imageHeight={319}
              data={{
                imageAlt: '',
                href: '/programas/festival-de-musica-radio-nacional',
                title: 'Ir para página do Festival de Música Rádio Nacional',
                linkAriaLabel:
                  'Acessar página do Festival de Música Rádio Nacional',
              }}
            />
          </div>
        </aside>
      </div>

      <div className="schedule-view__bottom">
        <PodcastEpisodesView data={{ title: 'Podcast', maxItems: 6 }} />
      </div>
    </div>
  );
}
